<template>
  <figure 
    class="sticky-questionnaire" 
    :class="{ 
      'expanded': isExpanded, 
      'submitted': isSubmitted,
      'sticky-questionnaire--closed': !isExpanded,
      'cookie-consent-rejected': !cookiesAccepted
    }"
  >
    <div 
      class="sticky-questionnaire__tab" 
      @click=" toggleExpand()"
    >
      <span v-if="!isSubmitted" class="sticky-questionnaire__tab-text">Fühlst du dich jetzt besser vorbereitet im nächsten Termin über deine HIV-Behandlung zu sprechen?</span>
      <span v-if="isSubmitted" class="sticky-questionnaire__tab-text">Danke für deine Bewertung!</span>
    </div>

    <section class="sticky-questionnaire__content">
      <button 
        class="sticky-questionnaire__close" 
        @click="toggleExpand()"
      >×</button>
      
      <div 
        class="sticky-questionnaire__content-wrapper"
        @click="!cookiesAccepted && openCookieSettings()"
      >
        <div v-if="!isSubmitted" class="sticky-questionnaire__form">
          <h3 class="sticky-questionnaire__question">Fühlst du dich jetzt besser vorbereitet im nächsten Termin über deine HIV-Behandlung zu sprechen?</h3>
          
          <div class="sticky-questionnaire__options">
            <label class="sticky-questionnaire__option">
              <input 
                type="radio" 
                name="empowerment" 
                value="Gering" 
                v-model="selectedOption"
                @change="handleOptionChange('Gering')"
                :disabled="!cookiesAccepted"
              />
              <span class="checkbox-custom"></span>
              Gering
            </label>
            
            <label class="sticky-questionnaire__option">
              <input 
                type="radio" 
                name="empowerment" 
                value="Mittel" 
                v-model="selectedOption"
                @change="handleOptionChange('Mittel')"
                :disabled="!cookiesAccepted"
              />
              <span class="checkbox-custom"></span>
              Mittel
            </label>
            
            <label class="sticky-questionnaire__option">
              <input 
                type="radio" 
                name="empowerment" 
                value="Sehr" 
                v-model="selectedOption"
                @change="handleOptionChange('Sehr')"
                :disabled="!cookiesAccepted"
              />
              <span class="checkbox-custom"></span>
              Sehr
            </label>
          </div>
          
          <button 
            class="sticky-questionnaire__submit" 
            @click="cookiesAccepted ? submitFeedback() : openCookieSettings()"
            :disabled="!selectedOption && cookiesAccepted"
          >
            Absenden
          </button>
        </div>
        
        <!-- Thank you message -->
        <div v-else class="sticky-questionnaire__thank-you">
          <p>Danke für Deine Bewertung!</p>
        </div>

        <!-- Cookie consent overlay -->
        <div v-if="!cookiesAccepted" class="cookie-consent-overlay" @click.prevent="openCookieSettings">
          <p>
            Hinweis: Um an der Umfrage teilnehmen zu können, akzeptiere bitte hier die funktionellen Cookies:
          </p>
          <a href="#" @click.prevent="openCookieSettings">Cookie-Einstellungen verwalten.</a>
        </div>
      </div>
    </section>
  </figure>
</template>

<script>
export default {
  name: 'StickyQuestionnaire',
  props: {
    articleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false,
      isSubmitted: false,
      selectedOption: null,
      automaticlyOpenOnScroll: true,
      cookieKey: 'feedback_submitted_',
      cookiesAccepted: false,
      oneTrustLoaded: false,
      oneTrustPollingInterval: null
    };
  },
  mounted() {
    this.checkPreviousSubmission();
    window.addEventListener('scroll', this.handleScroll);
    this.startOneTrustPolling();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    this.removeOneTrustListener();
    if (this.oneTrustPollingInterval) {
      clearInterval(this.oneTrustPollingInterval);
    }
  },
  watch: {
    oneTrustLoaded(newValue) {
      if (newValue) {
        this.setupOneTrustListener();
        this.checkCookieConsent();
      }
    }
  },
  methods: {
    /**
     * Starts polling for the OneTrust script to be loaded.
     * Sets an interval that checks every 100 milliseconds if the OneTrust script is available.
     * Once the OneTrust script is detected, sets `oneTrustLoaded` to true and clears the interval.
     */
    startOneTrustPolling() {
      this.oneTrustPollingInterval = setInterval(() => { // check for OneTrust script to be loaded
        if (window.OneTrust && window.OneTrust.GetDomainData) {
          this.oneTrustLoaded = true;
          clearInterval(this.oneTrustPollingInterval);
        }
      }, 100);
    },
    /**
     * Toggles the expansion state of the questionnaire.
     * When called, it switches the `isExpanded` property between true and false.
     */
    toggleExpand() {
        this.automaticlyOpenOnScroll = false;
        this.isExpanded = !this.isExpanded;
    },
    /**
     * Handles the scroll event to determine the scroll position as a percentage of the total document height.
     * If the scroll position is 50% or more, and the questionnaire is not expanded, not submitted, and cookies are accepted,
     * it sets the `isExpanded` property to true.
     */
    handleScroll() {
      const scrollPosition = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = (scrollPosition / documentHeight) * 100;
      
      if (scrollPercentage >= 50 && !this.isExpanded && !this.isSubmitted && this.cookiesAccepted && this.automaticlyOpenOnScroll) {
        this.isExpanded = true;
      }
    },
    /**
     * Handles the change of the selected option in the questionnaire.
     * 
     * @param {Object} option - The option that has been selected.
     */
    handleOptionChange(option) {
      this.selectedOption = option;
    },
    /**
     * Submits the feedback for the questionnaire.
     * 
     * - If Google Analytics (gtag) is available, it logs an event with the selected answer and article ID.
     * - Sets a cookie with the selected option and marks the feedback as submitted.
     */
    submitFeedback() {
      if (!this.selectedOption || !this.cookiesAccepted) return;
      
      if (window.gtag) {
        window.dataLayer.push({
          event: 'feedback_submission',
          question_id: 'empowerment',
          answer_selected: this.selectedOption,
          article_id: this.articleId,
          article_url: window.location.href
        });
      }
      
      this.setCookie(this.cookieKey + this.articleId, this.selectedOption, 30);
      this.isSubmitted = true;
      
      setTimeout(() => {
        this.isExpanded = false;
      }, 2500);
    },
    /**
     * Checks if the user has previously submitted the questionnaire.
     */
    checkPreviousSubmission() {
      const hasSubmitted = this.getCookie(this.cookieKey + this.articleId);
      if (hasSubmitted) {
        this.isSubmitted = true;
      }
    },
    /**
     * Sets a cookie with the specified name, value, and expiration days.
     * 
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} days - The number of days until the cookie expires.
     * 
     * @throws Will log an error to the console if the cookie cannot be set.
     */
    setCookie(name, value, days) {
      try {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "; expires=" + date.toUTCString();
        document.cookie = name + "=" + value + expires + "; path=/";
      } catch (e) {
        console.error('Could not set cookie', e);
      }
    },
    /**
     * Retrieves the value of a specified cookie by its name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|null} The value of the cookie if found, otherwise null.
     */
    getCookie(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },
    /**
     * Checks if the user has given consent for performance cookies (C0002).
     * 
     * This method retrieves the active cookie consent groups from the `OnetrustActiveGroups` variable
     * and checks if the performance cookies group ("C0002") is included in the active groups.
     * 
     * @returns {boolean} - Returns `true` if performance cookies consent is given, otherwise `false`.
     */
    checkCookieConsent() {
      // C0001 (Strictly Necessary), C0002 (Performance), C0003 (Functional), C0004 (Targeting)
      const activeGroups = OnetrustActiveGroups || "";
      this.cookiesAccepted =  activeGroups.includes("C0002");
      return this.cookiesAccepted;
    },
    /**
     * Opens the cookie settings dialog using the OneTrust library.
     */
    openCookieSettings() {
      if (window.OneTrust) {
        window.OneTrust.ToggleInfoDisplay();
      }
    },
    /**
     * Sets up a listener for OneTrust consent changes.
     */
    setupOneTrustListener() {
      if (window.OneTrust) {
        window.OneTrust.OnConsentChanged(this.handleConsentChange);
      }
    },
    /**
     * Removes the OneTrust consent change listener if it exists.
     */
    removeOneTrustListener() {
      if (window.OneTrust) {
        //window.OneTrust.RemoveOnConsentChangedHandler(this.handleConsentChange);
      }
    },
    /**
     * Handles the change in consent by checking the cookie consent status.
     */
    handleConsentChange() {
      this.checkCookieConsent();
    }
  }
}
</script>

<style scoped>
.sticky-questionnaire {
  position: fixed;
  right: 0;
  top: 70%;
  transform: translateY(-50%);
  z-index: 999;
  display: flex;
  flex-direction: row-reverse;
  transition: all 0.3s ease;
}
.sticky-questionnaire.submitted:not(.expanded) .sticky-questionnaire__tab,.sticky-questionnaire.submitted:not(.expanded) .sticky-questionnaire__content{
  background-color: #d1d1d1 !important;
}

.sticky-questionnaire__tab {
  background-color: #e73d50;
  color: white;
  padding: 15px;
  cursor: pointer;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  border-radius: 0px 2px 2px 0px;
}
.sticky-questionnaire__tab-text{
  text-align: center;
}

.sticky-questionnaire__content {
  background-color: #e73d50;
  color: white;
  width: 0;
  overflow: hidden;
  transition: width 0.3s ease;
  position: relative;
  border-radius: 0 0 0 0;
}

.expanded .sticky-questionnaire__content {
  width: 330px;
  padding: 0px;
}

.expanded .sticky-questionnaire__tab {
  display: none;
}

.sticky-questionnaire__close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: white;
  font-size: 35px;
  cursor: pointer;
  padding: 0;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sticky-questionnaire__close{
  z-index: 1000;
}
.cookie-consent-rejected .sticky-questionnaire__close{
  top: -2px;
}

.sticky-questionnaire__question {
  font-size: 18px;
  margin-bottom: 20px;
  font-weight: bold;
  text-align: left;
}

.sticky-questionnaire__options {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.sticky-questionnaire__option {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  position: relative;
  padding: 10px;
  flex: 1;
  text-align: center;
}

.sticky-questionnaire__option input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-custom {
  position: relative;
  height: 24px;
  width: 24px;
  background-color: transparent;
  border: 1px solid white;
  margin-right: 7px;
}

.sticky-questionnaire__option input:checked ~ .checkbox-custom {
  background-color: transparent;
}

.checkbox-custom:after {
  content: "";
  position: absolute;
  display: none;
}

.sticky-questionnaire__option input:checked ~ .checkbox-custom:after {
  display: block;
}

.sticky-questionnaire__option .checkbox-custom:after {
  left: 50%;
  top: 44%;
  width: 16px;
  height: 16px;
  border: none;
  transform: translate(-50%, -50%);
  content: "×";
  color: white;
  font-size: 20px;
  line-height: 16px;
  text-align: center;
}

.sticky-questionnaire__submit {
  background-color: #000;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-weight: bold;
  font-size: 15px;
  line-height: 21px;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.sticky-questionnaire__submit:disabled {
  cursor: not-allowed;
}

.sticky-questionnaire__thank-you {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 186px;
  font-weight: bold;
  font-size: 18px;
  text-align: center;
}
.sticky-questionnaire__thank-you p{
  margin-bottom: 0.5rem;
}

.cookie-consent-overlay {
  position: relative;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px;
  font-size: 11px;
  text-align: center;
}

.cookie-consent-overlay a {
  color: white;
  text-decoration: underline;
  font-weight: bold;
}

.cookie-consent-rejected.expanded .sticky-questionnaire__content{
  border: 1px solid #000;
}
.cookie-consent-rejected .sticky-questionnaire__content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.cookie-consent-rejected .cookie-consent-overlay {
  z-index: 2;
}


.cookie-consent-rejected .sticky-questionnaire__content-wrapper {
  cursor: pointer;
}

.cookie-consent-rejected .sticky-questionnaire__form,
.cookie-consent-rejected .sticky-questionnaire__thank-you {
  opacity: 0.5;
  pointer-events: none;
  padding: 12px;
}
.sticky-questionnaire__form{
  padding: 12px;
  padding-top: 50px;
  padding-bottom: 18.5px;
}

.cookie-consent-rejected .sticky-questionnaire__content::before {
  
}

.cookie-consent-overlay {
  position: relative;
  background-color: white;
  color: black;
  padding: 12.5px;
  font-size: 11px;
  text-align: left;
  z-index: 2;
}

.cookie-consent-overlay p {
  margin: 0 0 5px 0;
}

.cookie-consent-overlay a {
  color: black;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 768px) {
  .expanded .sticky-questionnaire__content {
    width: 330px;
  }
  
  .sticky-questionnaire__tab {
    height: 320px;
    padding: 10px;
  }
}

.sticky-questionnaire__option:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.sticky-questionnaire__submit:hover:not(:disabled) {
  background-color: #333;
  transform: translateY(-2px);
}

.sticky-questionnaire__submit:active:not(:disabled) {
  transform: translateY(0);
}
</style>


// WEBPACK FOOTER //
// public/js/components/content/StickyQuestionnaire.vue